import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { getLeasingData } from '../api/apiConfig';
import { MdReceipt } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import * as XLSX from 'xlsx';

const Leasing = () => {
  const { user: userData } = useUser();
  const [leasingData, setLeasingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupedData, setGroupedData] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (userData) {
      fetchLeasingData();
    }
  }, [userData]);

  const fetchLeasingData = async () => {
    try {
      setLoading(true);
      const response = await getLeasingData(userData);
      
      let data;
      if (response.body) {
        data = JSON.parse(response.body);
      } else {
        data = response;
      }
      
      if (Array.isArray(data)) {
        setLeasingData(data);
        groupDataByMonth(data);
      } else {
        setError('Error al cargar los datos');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const groupDataByMonth = (data) => {
    const grouped = data.reduce((acc, item) => {
      // Crear fecha sin ajuste de zona horaria
      const [year, month, day] = item.FECHA_ACTIVACION.split('-');
      const date = new Date(year, month - 1, day);
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!acc[monthYear]) {
        acc[monthYear] = [];
      }
      acc[monthYear].push(item);
      return acc;
    }, {});
    
    setGroupedData(grouped);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getMonthName = (monthYear) => {
    const [year, month] = monthYear.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-CL');
  };

  const filterData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;
    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.FECHA_ACTIVACION, item.NUMERO_CONTRATO, item.RUT_CLIENTE, item.EJECUTIVO, item.NOMBRE_CLIENTE, item.MONTO, item.MONEDA];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  const sortDataList = (list) => {
    if (!sortColumn || !list.length) return list;
    return [...list].sort((a, b) => {
      let aValue = a[sortColumn]; let bValue = b[sortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const filteredData = filterData(leasingData);
  const groupedFiltered = filteredData.reduce((acc, item) => {
    const [year, month] = item.FECHA_ACTIVACION.split('-');
    const monthYear = `${year}-${month}`;
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(item);
    return acc;
  }, {});

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
  };

  const handleSearchChange = (e) => { setSearchTerm(e.target.value); };
  const clearSearch = () => { setSearchTerm(''); };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const exportToExcel = () => {
    const dataToExport = filterData(leasingData);
    const exportData = dataToExport.map(item => ({
      'Fecha Activación': formatDate(item.FECHA_ACTIVACION),
      'N° Contrato': item.NUMERO_CONTRATO,
      'RUT Cliente': item.RUT_CLIENTE,
      'Ejecutivo': item.EJECUTIVO,
      'Nombre Cliente': item.NOMBRE_CLIENTE,
      'Monto': item.MONTO,
      'Moneda': item.MONEDA
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Seguimiento Leasing');
    
    XLSX.writeFile(wb, `seguimiento_leasing_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  if (loading) {
    return (
      <div className="panel-container">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Cargando datos...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-red-600">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Seguimiento Comercial Leasing</h1>
      </div>  

      {leasingData.length > 0 && (
        <div className="mb-4">
          <div className="relative" style={{ maxWidth: '500px' }}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
            </div>
            <input type="text" value={searchTerm} onChange={handleSearchChange} placeholder="Buscar en todas las columnas..." className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            {searchTerm && <button type="button" onClick={clearSearch} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda"><IoClose className="h-5 w-5" /></button>}
          </div>
          {searchTerm && <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>{filteredData.length === 0 ? <span>No se encontraron resultados para &quot;{searchTerm}&quot;</span> : <span>{filteredData.length} resultado{filteredData.length !== 1 ? 's' : ''} encontrado{filteredData.length !== leasingData.length ? ` de ${leasingData.length} registros` : ''}</span>}</div>}
        </div>
      )}

      {Object.keys(groupedData).length === 0 ? (
        <div className="seccion-datos">
          <p className="text-gray-600">No hay datos disponibles</p>
        </div>
      ) : Object.keys(groupedFiltered).length === 0 && searchTerm ? (
        <div className="seccion-datos">
          <p className="text-gray-600">No se encontraron resultados para &quot;{searchTerm}&quot;</p>
        </div>
      ) : (
        Object.entries(groupedFiltered)
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([monthYear, data]) => (
          <div key={monthYear} className="seccion-datos">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800 capitalize">
                {getMonthName(monthYear)}
              </h2>
              {leasingData.length > 0 && (
                <button
                  onClick={exportToExcel}
                  className="btn-primary"
                  style={{
                    background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    borderLeft: '3px solid var(--if-primary)'
                  }}
                >
                  <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
                  Exportar Excel
                </button>
              )}
            </div>
              
              <div className="tabla-container">
                <table className="w-full" style={{fontSize: '0.875rem', tableLayout: 'fixed', width: '100%'}}> 
                  <thead>
                    <tr>
                      <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FECHA_ACTIVACION')}>Fecha Activación {getSortIcon('FECHA_ACTIVACION')}</th>
                      <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('NUMERO_CONTRATO')}>N° Contrato {getSortIcon('NUMERO_CONTRATO')}</th>
                      <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('RUT_CLIENTE')}>RUT Cliente {getSortIcon('RUT_CLIENTE')}</th>
                      <th style={{width: '200px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('EJECUTIVO')}>Ejecutivo {getSortIcon('EJECUTIVO')}</th>
                      <th style={{width: '300px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('NOMBRE_CLIENTE')}>Nombre Cliente {getSortIcon('NOMBRE_CLIENTE')}</th>
                      <th style={{width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('MONTO')}>Monto {getSortIcon('MONTO')}</th>
                      <th style={{width: '80px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('MONEDA')}>Moneda {getSortIcon('MONEDA')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortDataList(data).map((item, index) => (
                      <tr key={index}>
                        <td>{formatDate(item.FECHA_ACTIVACION)}</td>
                        <td>{item.NUMERO_CONTRATO}</td>
                        <td>{item.RUT_CLIENTE}</td>
                        <td>{item.EJECUTIVO}</td>
                        <td>{item.NOMBRE_CLIENTE}</td>
                        <td style={{textAlign: 'right'}}>
                          {item.MONEDA === 'UF' ? 
                            new Intl.NumberFormat('es-CL', { minimumFractionDigits: 1 }).format(item.MONTO) :
                            formatCurrency(item.MONTO)
                          }
                        </td>
                        <td style={{textAlign: 'center'}}>{item.MONEDA}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
      )}
    </div>
  );
};

export default Leasing;